import { useState, useEffect, useRef } from 'react'
import { useAction } from 'convex/react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { AlertTriangle, ArrowLeft, ExternalLink, Loader2 } from 'lucide-react'
import rrwebPlayer from 'rrweb-player'
import 'rrweb-player/dist/style.css'

const POSTHOG_PROJECT_ID = '198125'

export default function AdminProjectReplayPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const { id: posthogSessionId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const getRecordingSnapshots = useAction(api.posthog.getRecordingSnapshots)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [events, setEvents] = useState<any[] | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<rrwebPlayer | null>(null)

  // Fetch events once
  useEffect(() => {
    if (!isAdmin || !posthogSessionId) return
    let cancelled = false

    setLoading(true)
    setError(null)

    getRecordingSnapshots({ posthogSessionId })
      .then((eventsJson) => {
        if (cancelled) return
        const parsed = JSON.parse(eventsJson)
        if (!parsed.length) {
          setError('No recording events found for this session')
        } else {
          setEvents(parsed)
        }
        setLoading(false)
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e.message || 'Failed to load recording')
        setLoading(false)
      })

    return () => { cancelled = true }
    // Only re-fetch if posthogSessionId changes, not on every getRecordingSnapshots ref change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, posthogSessionId])

  // Create player when events are available and container is mounted
  useEffect(() => {
    if (!events || !containerRef.current) return

    const container = containerRef.current
    container.innerHTML = ''

    const width = container.clientWidth
    const CONTROLLER_HEIGHT = 80
    const height = container.clientHeight - CONTROLLER_HEIGHT

    if (width <= 0 || height <= 0) return

    try {
      playerRef.current = new rrwebPlayer({
        target: container,
        props: {
          events,
          width,
          height,
          autoPlay: false,
          showController: true,
          speedOption: [1, 2, 4, 8],
          skipInactive: true,
        },
      })
    } catch (err: any) {
      console.error('[replay] Player creation failed:', err)
      setError(err.message || 'Player initialization failed')
    }

    return () => {
      if (playerRef.current) {
        ;(playerRef.current as any).$destroy()
        playerRef.current = null
      }
    }
  }, [events])

  // Handle resize
  useEffect(() => {
    if (!playerRef.current || !containerRef.current) return
    const observer = new ResizeObserver(() => {
      playerRef.current?.triggerResize()
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [events])

  if (!isAdmin) {
    return (
      <div className="flex h-full items-center justify-center text-neutral-500">
        Admin access required
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-neutral-950">
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-md p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-neutral-200">Session Recording</span>
          <span className="font-mono text-xs text-neutral-500">{posthogSessionId}</span>
          {events && (
            <span className="text-xs text-neutral-600">{events.length.toLocaleString()} events</span>
          )}
        </div>
        <a
          href={`https://us.posthog.com/project/${POSTHOG_PROJECT_ID}/replay/${posthogSessionId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          Open in PostHog
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {loading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-500" />
            <p className="text-xs text-neutral-600">Loading recording from PostHog...</p>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
            <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
            <p className="text-sm text-neutral-400">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 text-xs text-indigo-400 hover:text-indigo-300"
            >
              Go back
            </button>
          </div>
        )}

        <div
          ref={containerRef}
          className={`h-full w-full ${loading || error ? 'invisible' : ''}`}
        />
      </div>
    </div>
  )
}
